import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

/**
 * "Voice AI" listen button — mock playback using the browser's built-in
 * SpeechSynthesis API. No external dependency, no API key, no audio file
 * needed. Demonstrates the "Voice AI" claim concretely in live demos.
 */
export default function VoiceListenButton({ text, dense = false }) {
  const [playing, setPlaying] = useState(false);
  const [supported, setSupported] = useState(true);
  const utteranceRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      setSupported(false);
    }
    return () => {
      // Stop on unmount
      try { window.speechSynthesis?.cancel(); } catch {}
    };
  }, []);

  if (!supported) return null;

  const stopAny = () => {
    try { window.speechSynthesis.cancel(); } catch {}
  };

  const handleClick = (e) => {
    e.stopPropagation();
    if (playing) {
      stopAny();
      setPlaying(false);
      return;
    }
    stopAny(); // cancel any other utterance
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    u.pitch = 1.0;
    u.volume = 1.0;
    // Prefer an English India / English voice if available
    const voices = window.speechSynthesis.getVoices();
    const preferred =
      voices.find((v) => /en[-_]IN/i.test(v.lang)) ||
      voices.find((v) => /^en/i.test(v.lang));
    if (preferred) u.voice = preferred;
    u.onend = () => setPlaying(false);
    u.onerror = () => setPlaying(false);
    utteranceRef.current = u;
    window.speechSynthesis.speak(u);
    setPlaying(true);
  };

  const sz = dense ? "text-[9px] px-1.5 py-0.5 gap-1" : "text-[10px] px-2 py-0.5 gap-1";

  return (
    <button
      onClick={handleClick}
      data-testid="voice-listen-btn"
      className={`inline-flex items-center font-bold rounded-full transition-all ${
        playing
          ? "bg-violet-600 text-white shadow-md"
          : "bg-violet-100 text-violet-700 hover:bg-violet-200"
      } ${sz}`}
      title={playing ? "Stop playback" : "Listen to this story (Voice AI)"}
    >
      {playing ? (
        <>
          <Pause size={dense ? 8 : 10} />
          <Waveform />
          <span>Stop</span>
        </>
      ) : (
        <>
          <Play size={dense ? 8 : 10} fill="currentColor" />
          Listen
        </>
      )}
    </button>
  );
}

function Waveform() {
  // 3 animated bars
  return (
    <span className="inline-flex items-end gap-[2px] h-2.5">
      <span className="w-[2px] bg-white rounded-full animate-wave-1" style={{ height: "50%" }} />
      <span className="w-[2px] bg-white rounded-full animate-wave-2" style={{ height: "100%" }} />
      <span className="w-[2px] bg-white rounded-full animate-wave-3" style={{ height: "70%" }} />
    </span>
  );
}
